import React, { useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import "../style/components.css";
import { OvalStar } from "../constant";

const MainContainer = styled(Box)(({ theme }) => ({
  background: "#000",
  display: "flex",
  flexDirection: "column",
  padding: "60px 24px",
  position: "relative",
  overflow: "hidden",
  width: "100%",
  margin: "0 auto",

  "&::after": {
    content: '""',
    position: "absolute",
    width: "95%",
    height: "80%",
    right: "65%",
    top: "10%",
    bottom: "10%",
    transform: "translateX(-100%), translateY(-50%)",
    background:
      "radial-gradient(circle, rgba(99, 102, 241, 0.65) 0%, rgba(99, 102, 241, 0) 70%)",
    filter: "blur(100px)",
    //backdropFilter: "blur(500px)",
    zIndex: 0,
  },
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  position: "relative",
  zIndex: 1,
  display: "flex",
  flexDirection: "column",
}));

const CardsContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  display: "flex",
  gap: theme.spacing(2),
  [theme.breakpoints.down("md")]: {
    gap: theme.spacing(0),
    marginTop: theme.spacing(0),
  },
}));

const Card = styled(Box)(({ theme }) => ({
  background:
    "radial-gradient(90.16% 143.01% at 15.32% 21.04%, rgba(165, 239, 255, 0.2) 0%, rgba(110, 191, 244, 0.0447917) 77.08%, rgba(70, 144, 213, 0) 100%)",
  color: "white",
  borderRadius: 16,
  padding: theme.spacing(4),
  width: "100%",
  maxWidth: 380,
  minHeight: 500,
  display: "flex",
  flexDirection: "column",
  transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
  boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
  position: "relative",
  overflow: "hidden",
  marginTop: "40px",

  [theme.breakpoints.down("md")]: {
    maxWidth: "100%",
    minHeight: 450,
  },

  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 15px 30px rgba(0,0,0,0.3)",
    background: 'linear-gradient(262.41deg, #1DA1F2 -8.32%, #6843EC 93.3%)',

    "& img": {
      transform: "scale(0.65) translateY(20px)", // 130px * 0.65 ≈ 85px
      width: "85px",
      height: "85px",
    },

    "& .content": {
      transform: "translateY(-50px)",
      [theme.breakpoints.down("md")]: {
        transform: "translateY(-10px)",
      },
    },

    "& .data-content": {
      opacity: 1,
      maxHeight: "200px",
      marginTop: "20px",
      color: '#fff',
    }
  },
}));

const AnimatedCardCrausal = ({ title, subTitle, data }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleSlideChange = (direction) => {
    setActiveIndex((prev) =>
      direction === "prev" ?
        (prev === 0 ? data.length - 1 : prev - 1) :
        (prev === data.length - 1 ? 0 : prev + 1)
    );
  };

  const getVisibleCards = () => {
    if (isMobile) {
      return data?.slice(activeIndex, activeIndex + 1)?.concat(data?.slice(0, Math.max(0, activeIndex + 1 - data.length)))?.slice(0, 1);
    }
    else return data?.slice(activeIndex, activeIndex + 3)?.concat(data?.slice(0, Math.max(0, activeIndex + 3 - data.length)))?.slice(0, 3);
  };

  return (
    <MainContainer>
      <ContentContainer>
        <Box sx={{
          width: { xs: "100%", md: "70%" },
          textAlign: "left",
          mb: 4
        }}>
          <Typography variant="h2" sx={{
            background: "linear-gradient(135deg, #6366F1 0%, #3B82F6 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontSize: { xs: 24, sm: 32, md: 40 },
            fontWeight: 700,
          }}>
            {title}
          </Typography>
          
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },  // Stack on mobile, row on desktop
            alignItems: { xs: "center", md: "center" },  // Center on mobile, align in line on desktop
            justifyContent: "space-between",  // Typography left, buttons right on desktop
            width: "100%",
            gap: { xs: 2, md: 0 },  // Space between elements on mobile
          }}
        >
          <Typography sx={{
            color: "#8C8CC2",
            fontSize: { xs: 14, md: 18 },
            lineHeight: 1.6,
            width:{xs:"100%",md:"70%"}
          }}>
            {subTitle}
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 3,
              width: { xs: "100%", md: "auto" },  // Full width on mobile, auto on desktop
              justifyContent: { xs: "center", md: "flex-end" },  // Centered on mobile, right on desktop
              mb: 4,
            }}
          >
            <button onClick={() => handleSlideChange("prev")} className="circular-btn">
              &lt;
            </button>
            <button onClick={() => handleSlideChange("next")} className="circular-btn">
              &gt;
            </button>
          </Box>
        </Box>


        <Box className="animatedCardC-section">
          <CardsContainer>
            {getVisibleCards()?.map((card, index) => (
              <Card key={index}>
                <img src={card?.icon ? card.icon:OvalStar} alt="" style={{
                  width: 130,
                  height: 130,
                  transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
                  transform: "translateY(0)",
                  //for smooth dimension transitions
                  transformOrigin: "top center",
                  willChange: "transform, width, height",
                }} />

                <Box className="content" sx={{
                  transition: "transform 0.8s ease",
                  textAlign: "left",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                  transform: "translateY(10px)"
                }}>
                  <Typography variant="h4" sx={{
                    color: "#fff",
                    fontSize: { xs: 20, md: 24 },
                    fontWeight: 600,
                    mb: 2,
                    transition: "all 1s ease"
                  }}>
                    {card?.title}
                  </Typography>

                  <Typography variant="body1" sx={{
                    color: "#E0E0FF",
                    fontSize: { xs: 14, md: 16 },
                    lineHeight: 1.6,
                    transition: "all 1s ease",
                    mb: 2
                  }}>
                    {card?.description}
                  </Typography>

                  <Typography className="data-content" sx={{
                    color: "#A0A0FF",
                    fontSize: 14,
                    opacity: 0,
                    maxHeight: 0,
                    transition: "all 0.5s ease",
                    overflow: "hidden",
                    mt: 2
                  }}>
                    {card?.data}
                  </Typography>
                </Box>
              </Card>
            ))}
          </CardsContainer>
        </Box>


      </ContentContainer>
    </MainContainer>
  );
};

export default AnimatedCardCrausal;